import { useSyncExternalStore } from 'react';

/** The shell's phone threshold (shell.css: `@container shell (max-width: 619px)`). */
const NARROW_WIDTH = 620;
const NARROW_QUERY = `(max-width: ${NARROW_WIDTH - 1}px)`;

/** The `shell` container (shell.css `.app-shell`), the box every `/shell:` query measures. */
function shellBox(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.querySelector('.app-shell');
}

function subscribe(onChange: () => void): () => void {
  const stops: (() => void)[] = [];
  if (typeof window.matchMedia === 'function') {
    const query = window.matchMedia(NARROW_QUERY);
    query.addEventListener('change', onChange);
    stops.push(() => query.removeEventListener('change', onChange));
  }
  const shell = shellBox();
  if (shell !== null && typeof ResizeObserver === 'function') {
    const observer = new ResizeObserver(onChange);
    observer.observe(shell);
    stops.push(() => observer.disconnect());
  }
  return () => {
    for (const stop of stops) stop();
  };
}

function snapshot(): boolean {
  // The shell's own width when it is laid out; a box with no width yet (or no shell at all, as in
  // a page rendered on its own) falls back to the viewport.
  const width = shellBox()?.clientWidth ?? 0;
  if (width > 0) return width < NARROW_WIDTH;
  return typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
}

/**
 * True on a phone-width shell. The switch the lists make in markup (not just in CSS), so that
 * exactly one of the wide and narrow controls is ever in the accessibility tree. It measures the
 * same box as the CSS (`@container shell`, #956 follow-up), so the markup and the styles never
 * disagree about which layout is on; the viewport is only the fallback outside the shell.
 */
export function useNarrow(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}
